import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { SALayout } from "../../components/superadmin/SALayout";
import { formatCurrency, formatDate } from "../../lib/format";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { IndianRupee, Users, Beaker, AlertTriangle } from "lucide-react";

const PAYMENT_OPTS = ["All", "Paid", "Overdue", "Failed", "Trial"];

export default function SABilling() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/admin/billing").then(({ data }) => setData(data));
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    let r = [...data.venues];
    if (filter !== "All") r = r.filter((v) => v.payment_status === filter);
    return r.sort((a, b) => (b.monthly_amount || 0) - (a.monthly_amount || 0));
  }, [data, filter]);

  if (!data) return <SALayout><div className="text-[#A0A0A0]">Loading…</div></SALayout>;

  return (
    <SALayout>
      <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-6">Billing Overview</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="MRR" value={formatCurrency(data.mrr)} icon={IndianRupee} />
        <Stat label="Paying Venues" value={data.paying_count} icon={Users} />
        <Stat label="Trial Venues" value={data.trial_count} icon={Beaker} />
        <Stat label="Overdue / Failed" value={data.overdue_count} icon={AlertTriangle} accent />
      </div>

      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 mb-6">
        <div className="text-sm font-semibold mb-3">MRR Trend — Last 12 Months</div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data.months}>
            <CartesianGrid stroke="#ffffff10" />
            <XAxis dataKey="month" stroke="#A0A0A0" fontSize={11} />
            <YAxis stroke="#A0A0A0" fontSize={11} />
            <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #9D00FF33" }} />
            <Line type="monotone" dataKey="mrr" stroke="#9D00FF" strokeWidth={2} dot={{ r: 3, fill: "#9D00FF" }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="text-sm font-semibold">Billing by Venue</div>
          <select
            data-testid="sa-billing-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-xs text-white"
          >
            {PAYMENT_OPTS.map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="sa-billing-table">
            <thead className="bg-[#0F0F0F] text-[#A0A0A0] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Venue</th>
                <th className="px-4 py-3 text-left">Plan</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Amount</th>
                <th className="px-4 py-3 text-left">Payment</th>
                <th className="px-4 py-3 text-left">Next Billing</th>
                <th className="px-4 py-3 text-left">Discount</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((v) => (
                <tr key={v.id} className="hover:bg-white/5">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-white">{v.name}</div>
                    <div className="text-[10px] text-[#A0A0A0]">{v.city || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-white/90">{v.plan}</td>
                  <td className="px-4 py-3 text-white/90">{v.status}</td>
                  <td className="px-4 py-3 text-white/90">{formatCurrency(v.monthly_amount)}</td>
                  <td className="px-4 py-3"><PaymentChip s={v.payment_status} /></td>
                  <td className="px-4 py-3 text-white/90">{formatDate(v.next_billing_date)}</td>
                  <td className="px-4 py-3 text-white/90">{v.discount || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/venues/${v.id}`}
                      className="text-[#9D00FF] text-xs font-semibold hover:underline"
                    >Manage →</Link>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-[#A0A0A0]">No venues.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </SALayout>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-[#A0A0A0]">{label}</div>
        <Icon size={16} className={accent ? "text-[#EF4444]" : "text-[#9D00FF]"} />
      </div>
      <div className="text-3xl font-black text-white">{value}</div>
    </div>
  );
}
function PaymentChip({ s }) {
  const map = {
    Paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    Trial: "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40",
    Overdue: "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/40",
    Failed: "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/40",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${map[s] || "bg-white/10 text-white border-white/20"}`}>{s || "—"}</span>;
}
